import { EnvironmentName } from '@commenda/auth/internal/constants';
import { Dialog, DialogContent, DialogTitle } from '@commenda/kakashi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnnouncementModal } from './AnnouncementModal';
import { DetailView } from './DetailView';
import { ListView } from './ListView';
import { ChangelogEntry } from './types';
import { getUnseenEntries, LAST_SEEN_CHANGELOG_KEY } from './utils';

/** "What's new" changelog surface, launched from the profile dropdown. Owns the changelog fetch,
 * unseen-tracking and the auto-announcement modal (unchanged); the browsable list/detail panel is a
 * controlled dialog driven by `isOpen`. */
export function ProductChangelogWidget({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [changelogs, setChangelogs] = useState<ChangelogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<ChangelogEntry | null>(
    null
  );
  const [announcementEntries, setAnnouncementEntries] = useState<
    ChangelogEntry[]
  >([]);
  const hasCheckedRef = useRef(false);

  const markAsSeen = useCallback((id: string) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(LAST_SEEN_CHANGELOG_KEY, id);
    } catch {
      // ignore localStorage errors (e.g. private mode, quota, disabled)
    }
  }, []);

  const fetchChangelogs = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      const res = await fetch('/api/changelogs');
      const data = await res.json();
      if (Array.isArray(data)) {
        const published = data.filter(
          (entry: ChangelogEntry) => entry.published
        );
        setChangelogs(published.slice(0, 2));

        // Surface unseen published changelogs in the announcement modal — first fetch only.
        if (!hasCheckedRef.current && published.length > 0) {
          hasCheckedRef.current = true;
          if (typeof window !== 'undefined') {
            try {
              const lastSeenId = localStorage.getItem(LAST_SEEN_CHANGELOG_KEY);
              const latest = published[0];
              if (lastSeenId !== latest.id) {
                setAnnouncementEntries(getUnseenEntries(published, lastSeenId));
              }
            } catch {
              // ignore localStorage errors (e.g. private mode, disabled)
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Prefetch on mount so data is ready when the panel opens.
  useEffect(() => {
    fetchChangelogs(true);
  }, [fetchChangelogs]);

  // Silently refresh when the panel opens to pick up newly published entries.
  useEffect(() => {
    if (isOpen) fetchChangelogs();
  }, [isOpen, fetchChangelogs]);

  // Reset to list view when the panel closes.
  useEffect(() => {
    if (!isOpen) setSelectedEntry(null);
  }, [isOpen]);

  // Suppress the auto-announcement in the demo environment — it overlaps the quick-demo signup
  // modal on `/` (both render at z-50).
  const isDemoEnv = process.env.NEXT_PUBLIC_CLIENT_ENV === EnvironmentName.DEMO;

  const handleAnnouncementClose = useCallback(
    (lastViewed: ChangelogEntry) => {
      markAsSeen(lastViewed.id);
      setAnnouncementEntries([]);
    },
    [markAsSeen]
  );

  return (
    <>
      {/* Announcement modal — auto-shows on any page when there are unseen published changelogs.
          Suppressed in demo (collides with the quick-demo signup modal). */}
      {!isDemoEnv && announcementEntries.length > 0 && (
        <AnnouncementModal
          entries={announcementEntries}
          onClose={handleAnnouncementClose}
        />
      )}

      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          preventCloseOnOutsideClick={false}
          className="w-[480px] max-w-[95vw] p-0"
        >
          <DialogTitle className="sr-only">What&apos;s new</DialogTitle>
          {selectedEntry ? (
            <DetailView
              entry={selectedEntry}
              onBack={() => setSelectedEntry(null)}
            />
          ) : (
            <div className="p-3xl">
              <ListView
                changelogs={changelogs}
                isLoading={isLoading}
                onSelect={setSelectedEntry}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
